import React from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { ArrowRight, Zap, MapPin, Plug, Sun, Wind, Mountain, Droplets } from "lucide-react";
import MapExplore from "./MapExplore";

const HERO_IMG =
  "https://images.pexels.com/photos/16586150/pexels-photo-16586150.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const WIND_IMG =
  "https://images.unsplash.com/photo-1587168173357-99c7e95fb5cd?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzN8MHwxfHNlYXJjaHw0fHx3aW5kJTIwdHVyYmluZXMlMjBsYW5kc2NhcGV8ZW58MHx8fHwxNzkyMzYxMDkwfDA&ixlib=rb-4.1.0&q=85";
const HYDRO_IMG =
  "https://images.unsplash.com/photo-1560279966-2d681f3d4dfc?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2MzR8MHwxfHNlYXJjaHwyfHxoeWRybyUyMGVsZWN0cmljJTIwZGFtfGVufDB8fHx8MTc5MjM2MTA5MHww&ixlib=rb-4.1.0&q=85";

export default function Landing() {
  return (
    <div data-testid="landing-page">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 pt-8 sm:pt-16 pb-12 sm:pb-20 grid lg:grid-cols-12 gap-8 lg:gap-10 items-center">
          <div className="lg:col-span-7">
            <Badge
              variant="outline"
              className="rounded-full px-3 py-1 border-primary/30 text-primary bg-primary/5"
              data-testid="hero-badge"
            >
              India's renewable energy marketplace
            </Badge>
            <h1 className="font-display mt-6 text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold leading-[0.95] tracking-tight text-primary">
              Projects, land, grid<br />
              <span className="text-accent">and power</span> — in one marketplace.
            </h1>
            <p className="mt-6 text-base sm:text-lg text-muted-foreground max-w-xl leading-relaxed">
              List or discover solar, wind, hybrid and hydro opportunities across India.
              Transact state-wise RE power, bid for CTU/STU connectivity, and find
              land ready for green parks — all in one trusted place.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/browse" className="w-full sm:w-auto">
                <Button size="lg" data-testid="hero-browse-btn" className="w-full sm:w-auto bg-primary hover:bg-primary/90 gap-2">
                  Browse marketplace <ArrowRight className="h-4 w-4" />
                </Button>
              </Link>
              <Link to="/register" className="w-full sm:w-auto">
                <Button size="lg" variant="outline" data-testid="hero-list-btn" className="w-full sm:w-auto gap-2">
                  List your opportunity
                </Button>
              </Link>
            </div>
            <div className="mt-10 flex flex-wrap items-center gap-6 sm:gap-8 text-sm">
              <div>
                <div className="font-display text-3xl font-bold text-primary">4</div>
                <div className="text-muted-foreground">Listing categories</div>
              </div>
              <div>
                <div className="font-display text-3xl font-bold text-primary">36</div>
                <div className="text-muted-foreground">States & UTs</div>
              </div>
              <div>
                <div className="font-display text-3xl font-bold text-primary">100%</div>
                <div className="text-muted-foreground">Admin-approved listings</div>
              </div>
            </div>
          </div>
          <div className="lg:col-span-5 relative">
            <div className="relative rounded-3xl overflow-hidden noise aspect-[4/3] sm:aspect-[16/10] lg:aspect-[4/5]">
              <img src={HERO_IMG} alt="Solar farm" className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-primary/60 via-primary/10 to-transparent" />
              <div className="absolute bottom-6 left-6 right-6 text-white">
                <div className="label-caps text-white/70">Featured</div>
                <div className="font-display text-2xl font-bold leading-tight mt-1">
                  Rajasthan 250 MW Solar Park
                </div>
                <div className="text-sm text-white/80 mt-1">Operational · CTU connected</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* MAP */}
      <section id="map" className="border-y border-border bg-card/40">
        <MapExplore />
      </section>

      {/* BENTO CATEGORIES */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6 pt-12 sm:pt-16 pb-12 sm:pb-16">
        <div className="flex items-end justify-between mb-8">
          <div>
            <div className="label-caps">What's on the market</div>
            <h2 className="font-display text-3xl md:text-5xl font-bold text-primary tracking-tight mt-2">
              Four listing categories.
            </h2>
          </div>
          <Link to="/browse" className="hidden md:inline text-sm font-medium text-primary hover:underline">
            View all →
          </Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-6 gap-4 auto-rows-[minmax(200px,auto)] md:auto-rows-[220px]">
          <Link
            to="/browse?category=project"
            data-testid="cat-projects"
            className="md:col-span-4 md:row-span-2 relative overflow-hidden rounded-3xl group bg-primary"
          >
            <img
              src={WIND_IMG}
              alt="Wind projects"
              className="absolute inset-0 w-full h-full object-cover opacity-70 group-hover:opacity-80 transition-opacity"
            />
            <div className="absolute inset-0 bg-gradient-to-br from-primary/80 via-primary/40 to-transparent" />
            <div className="relative h-full p-8 flex flex-col justify-between text-white">
              <div className="flex gap-2">
                <span className="h-10 w-10 grid place-items-center rounded-full bg-white/15 backdrop-blur">
                  <Sun className="h-5 w-5" />
                </span>
                <span className="h-10 w-10 grid place-items-center rounded-full bg-white/15 backdrop-blur">
                  <Wind className="h-5 w-5" />
                </span>
                <span className="h-10 w-10 grid place-items-center rounded-full bg-white/15 backdrop-blur">
                  <Droplets className="h-5 w-5" />
                </span>
              </div>
              <div>
                <div className="label-caps text-white/70">01 — Projects</div>
                <h3 className="font-display text-3xl md:text-5xl font-bold mt-2 tracking-tight">
                  Renewable projects
                </h3>
                <p className="mt-2 text-white/80 max-w-md">
                  Operational, under-construction or planned solar, wind, hybrid and hydro.
                </p>
              </div>
            </div>
          </Link>

          <Link
            to="/browse?category=land"
            data-testid="cat-land"
            className="md:col-span-2 relative overflow-hidden rounded-3xl group bg-secondary border border-border"
          >
            <div className="h-full p-6 flex flex-col justify-between">
              <Mountain className="h-7 w-7 text-primary" />
              <div>
                <div className="label-caps">02 — Land</div>
                <h3 className="font-display text-2xl font-bold text-primary mt-1 tracking-tight">
                  Land parcels
                </h3>
                <p className="text-sm text-muted-foreground mt-1">
                  Acres ready for RE parks across India.
                </p>
              </div>
            </div>
          </Link>

          <Link
            to="/browse?category=connectivity"
            data-testid="cat-connectivity"
            className="md:col-span-2 relative overflow-hidden rounded-3xl group bg-accent/90"
          >
            <div className="h-full p-6 flex flex-col justify-between text-accent-foreground">
              <Plug className="h-7 w-7" />
              <div>
                <div className="label-caps text-accent-foreground/70">03 — Grid</div>
                <h3 className="font-display text-2xl font-bold mt-1 tracking-tight">
                  CTU / STU connectivity
                </h3>
                <p className="text-sm opacity-80 mt-1">
                  Bay allocations &amp; transmission access.
                </p>
              </div>
            </div>
          </Link>

          <Link
            to="/browse?category=power"
            data-testid="cat-power"
            className="md:col-span-4 relative overflow-hidden rounded-3xl group bg-primary/5 border border-primary/10"
          >
            <img
              src={HYDRO_IMG}
              alt="Hydro"
              className="hidden sm:block absolute right-0 top-0 h-full w-1/2 object-cover opacity-70 group-hover:opacity-90 transition-opacity"
            />
            <div className="relative h-full p-6 flex flex-col justify-between max-w-md sm:max-w-[48%]">
              <Zap className="h-7 w-7 text-primary" />
              <div>
                <div className="label-caps">04 — Trading</div>
                <h3 className="font-display text-3xl font-bold text-primary mt-1 tracking-tight">
                  Short-term RE power
                </h3>
                <p className="text-sm text-muted-foreground mt-1">
                  State-wise short-term renewable power — buy &amp; sell.
                </p>
              </div>
            </div>
          </Link>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6 py-12 sm:py-16">
        <div className="label-caps">How it works</div>
        <h2 className="font-display text-3xl md:text-5xl font-bold text-primary tracking-tight mt-2 max-w-3xl">
          A verified marketplace built for serious counterparties.
        </h2>
        <div className="mt-10 grid md:grid-cols-3 gap-6">
          {[
            {
              n: "01",
              title: "Post or discover",
              body:
                "Sellers list projects, land, connectivity or short-term power. Buyers filter by state, technology and capacity.",
            },
            {
              n: "02",
              title: "Admin approval",
              body:
                "Every listing is reviewed and approved by our admin team before it goes live, so members only see genuine opportunities.",
              icon: <MapPin className="h-5 w-5" />,
            },
            {
              n: "03",
              title: "Transact with confidence",
              body:
                "Members see the poster's contact details on every listing and can reach out directly or send an inquiry.",
            },
          ].map((s) => (
            <div
              key={s.n}
              className="rounded-2xl border border-border bg-card p-6 hover:-translate-y-1 hover:shadow-sm transition-all"
            >
              <div className="label-caps text-accent">{s.n}</div>
              <h3 className="font-display text-xl font-semibold mt-2" dangerouslySetInnerHTML={{ __html: s.title }} />
              <p className="text-sm text-muted-foreground mt-2" dangerouslySetInnerHTML={{ __html: s.body }} />
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
